import {
  buildLocatorFromViewerState,
  LocatorParseError,
  parseLocator,
  renderLocator,
} from '@/components/chat-v2/lib/locator'
import type { PanelViewerState } from '@/components/chat-v2/lib/panelEntrypoints'
import type { PdfSelectionPageRects } from '@/components/common/PdfViewerV2/PdfViewer'

import type { ViewportViewerState } from '@/types/chat-v2/sessions'

/**
 * SELECTION LOCATOR DSL — where a selection card reopens, one string per card.
 *
 * A chat-panel superset of the canonical citation locator DSL (locator.ts,
 * grammar-locked to markit `anchors.py`). Canonical forms pass through
 * unchanged; the extensions below exist only inside the chat panel
 * (`card.location.locator`) and MUST NOT be sent to markit.
 *
 * Exactly one family per viewer. Parse with `parseSelectionLocator`, switch
 * on `.viewer` — the locator decides which viewer restores the selection,
 * never the file type (a Word file pre-superdoc carries `pdf:*` forms
 * because its selection was made on the PDF rendition):
 *
 *   viewer | form                                          | restore
 *   -------|-----------------------------------------------|--------------------------
 *   pdf    | `pdf:p12`                                     | jump to page, re-find text
 *   pdf    | `pdf:bbox(12:l,t,w,h[;12:l,t,w,h;13:...])`    | repaint the exact boxes
 *   excel  | `xlsx:'Q3 Costs'!B2:D7`                       | re-select the range
 *   pptx   | `pptx:s3` / `pptx:s3/sh14`                    | reveal slide (and shape)
 *   docx   | `docx:para(1A2B3C4D)` / `docx:p42`            | superdoc paragraph (GA)
 *   text   | `txt:plain`                                   | re-find the captured text
 *
 * `pdf:bbox` boxes are page-local scale-1 units (the space PdfSearchHighlights
 * paints in): `page:left,top,width,height`, semicolon-separated, coordinates
 * non-negative with at most two decimals, pages ascending. Boxes are grouped
 * per page on parse.
 */
export type SelectionLocatorTarget =
  | { viewer: 'pdf'; kind: 'bbox'; pages: PdfSelectionPageRects[] }
  | { viewer: 'pdf'; kind: 'page'; page: number }
  | { viewer: 'excel'; kind: 'range'; sheetName: string; cellRange: string }
  | { viewer: 'pptx'; kind: 'slide'; slideIndex: number; shapeId?: number }
  | {
      viewer: 'docx'
      kind: 'paragraph'
      paragraphId?: string
      paragraphIndex?: number
    }
  | { viewer: 'text'; kind: 'plain' }

const TEXT_SELECTION_LOCATOR = 'txt:plain'

// One box: `page:left,top,width,height`. Coordinates are non-negative, no
// leading zeros on the integer part, at most two decimals.
const BBOX_COORD = String.raw`(?:0|[1-9][0-9]*)(?:\.[0-9]{1,2})?`
const BBOX_BOX = String.raw`([1-9][0-9]*):(${BBOX_COORD}),(${BBOX_COORD}),(${BBOX_COORD}),(${BBOX_COORD})`
const CANON_PDF_BBOX = new RegExp(
  String.raw`^pdf:bbox\(${BBOX_BOX}(?:;${BBOX_BOX})*\)$`,
)
const BBOX_BOX_G = new RegExp(BBOX_BOX, 'g')

const renderBboxCoord = (value: number): string => {
  const clamped = Math.max(0, value)
  // Two decimals, then strip trailing zeros so re-rendering stays canonical.
  return String(Number(clamped.toFixed(2)))
}

/** `pdf:bbox(…)` for the captured boxes; null when there is nothing to bake. */
export const renderPdfBboxLocator = (
  pages: readonly PdfSelectionPageRects[],
): string | null => {
  const boxes = [...pages]
    .sort((a, b) => a.pageNumber - b.pageNumber)
    .flatMap((page) =>
      page.rects.map(
        (rect) =>
          `${page.pageNumber}:${renderBboxCoord(rect.left)},${renderBboxCoord(
            rect.top,
          )},${renderBboxCoord(rect.width)},${renderBboxCoord(rect.height)}`,
      ),
    )
  if (boxes.length === 0) return null
  return `pdf:bbox(${boxes.join(';')})`
}

const parsePdfBboxLocator = (text: string): PdfSelectionPageRects[] | null => {
  if (!CANON_PDF_BBOX.test(text)) return null
  const pagesInOrder: PdfSelectionPageRects[] = []
  const byPage = new Map<number, PdfSelectionPageRects>()
  for (const match of text.matchAll(BBOX_BOX_G)) {
    const pageNumber = Number(match[1])
    const rect = {
      left: Number(match[2]),
      top: Number(match[3]),
      width: Number(match[4]),
      height: Number(match[5]),
    }
    const page = byPage.get(pageNumber)
    if (page) {
      page.rects.push(rect)
    } else {
      const created = { pageNumber, rects: [rect] }
      byPage.set(pageNumber, created)
      pagesInOrder.push(created)
    }
  }
  return pagesInOrder.length > 0 ? pagesInOrder : null
}

/**
 * The selection locator for a captured viewer state, canonical-first:
 * a PDF capture that measured its highlight boxes bakes them in as
 * `pdf:bbox`, a text capture is marked restorable-by-text, and every other
 * viewer keeps its canonical citation locator.
 *
 * `viewer` is the wire-shaped viewport state (drives canonical forms);
 * `raw` is the local panel capture that carries what the wire does not
 * (the measured boxes).
 */
export const buildSelectionLocator = (
  viewer: ViewportViewerState | undefined,
  raw: PanelViewerState | undefined,
): string | null => {
  if (
    viewer?.kind === 'pdf' &&
    raw?.kind === 'pdf' &&
    raw.selectionPageRects &&
    raw.selectionPageRects.length > 0
  ) {
    const bbox = renderPdfBboxLocator(raw.selectionPageRects)
    if (bbox) return bbox
  }
  if (viewer?.kind === 'text') return TEXT_SELECTION_LOCATOR
  return buildLocatorFromViewerState(viewer)
}

/**
 * Parse any selection locator (superset first, then the canonical DSL) into
 * the viewer-discriminated target. Null for unrecognized strings — the
 * caller falls back to a plain open.
 */
export const parseSelectionLocator = (
  text: string,
): SelectionLocatorTarget | null => {
  const trimmed = text.trim()
  const bboxPages = parsePdfBboxLocator(trimmed)
  if (bboxPages) return { viewer: 'pdf', kind: 'bbox', pages: bboxPages }
  if (trimmed === TEXT_SELECTION_LOCATOR)
    return { viewer: 'text', kind: 'plain' }
  try {
    const locator = parseLocator(trimmed)
    switch (locator.kind) {
      case 'page':
        return { viewer: 'pdf', kind: 'page', page: locator.page }
      case 'sheet':
        return {
          viewer: 'excel',
          kind: 'range',
          sheetName: locator.sheetName,
          cellRange: locator.cellRange,
        }
      case 'slide':
        return {
          viewer: 'pptx',
          kind: 'slide',
          slideIndex: locator.slideIndex,
          ...(locator.shapeId !== undefined
            ? { shapeId: locator.shapeId }
            : {}),
        }
      case 'paragraph':
        return {
          viewer: 'docx',
          kind: 'paragraph',
          ...(locator.paragraphId !== undefined
            ? { paragraphId: locator.paragraphId }
            : {}),
          ...(locator.paragraphIndex !== undefined
            ? { paragraphIndex: locator.paragraphIndex }
            : {}),
        }
    }
  } catch (error) {
    if (error instanceof LocatorParseError) return null
    throw error
  }
  return null
}
/**
 * Downgrade a selection locator to the canonical citation DSL for
 * agent-facing text (sent messages). Chat-only forms lose their extra
 * precision — `pdf:bbox` keeps its page span but drops the boxes
 * (`pdf:p4-p6`), `txt:plain` (and anything unrecognized) becomes nothing.
 * Canonical forms pass through untouched.
 */
export const canonicalizeSelectionLocator = (text: string): string | null => {
  const target = parseSelectionLocator(text)
  if (!target) return null
  switch (target.viewer) {
    case 'pdf': {
      if (target.kind !== 'bbox') return text.trim()
      const pages = target.pages.map((group) => group.pageNumber)
      const first = Math.min(...pages)
      const last = Math.max(...pages)
      return renderLocator({
        kind: 'page',
        page: first,
        ...(last > first ? { endPage: last } : {}),
      })
    }
    case 'excel':
    case 'pptx':
    case 'docx':
      return text.trim()
    case 'text':
      return null
    default:
      return target satisfies never
  }
}
